import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";

let initialized = false;
let logsInitialized = false;

/**
 * Patterns for detecting common PII in strings
 */
const PII_PATTERNS = [
  // Email addresses
  {
    pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    replacement: "[EMAIL_REDACTED]",
  },
  // SSN (XXX-XX-XXXX or XXXXXXXXX)
  { pattern: /\b\d{3}-\d{2}-\d{4}\b/g, replacement: "[SSN_REDACTED]" },
  { pattern: /\b\d{9}\b/g, replacement: "[SSN_REDACTED]" },
  // Phone numbers (various formats)
  {
    pattern: /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,
    replacement: "[PHONE_REDACTED]",
  },
  // Credit card numbers (13-19 digits, with or without spaces/dashes)
  {
    pattern: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
    replacement: "[CC_REDACTED]",
  },
  // JWT tokens (Bearer tokens)
  {
    pattern: /Bearer\s+[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+/g,
    replacement: "Bearer [TOKEN_REDACTED]",
  },
  // API keys (common patterns: 32+ alphanumeric characters)
  { pattern: /\b[A-Za-z0-9]{32,}\b/g, replacement: "[API_KEY_REDACTED]" },
];

/**
 * Sensitive field names to redact from objects
 */
const SENSITIVE_KEYS = [
  "password",
  "token",
  "apiKey",
  "api_key",
  "accessToken",
  "access_token",
  "refreshToken",
  "refresh_token",
  "secret",
  "ssn",
  "social_security",
  "creditCard",
  "credit_card",
  "cvv",
  "pin",
];

/**
 * Redact PII from a string
 */
function redactPII(text: string): string {
  if (!text || typeof text !== "string") {
    return text;
  }

  let redacted = text;
  for (const { pattern, replacement } of PII_PATTERNS) {
    redacted = redacted.replace(pattern, replacement);
  }
  return redacted;
}

/**
 * Redact sensitive query parameters from URLs
 */
function redactUrlParams(url: string): string {
  try {
    const urlObj = new URL(url);
    // Remove all query parameters to be safe
    urlObj.search = "";
    return urlObj.toString();
  } catch {
    return url;
  }
}

/**
 * Redact PII from object properties (recursive)
 */
function redactObjectPII(
  obj: Record<string, unknown>,
): Record<string, unknown> {
  const redacted: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Check if key is sensitive
    const isSensitiveKey = SENSITIVE_KEYS.some((sensitiveKey) =>
      key.toLowerCase().includes(sensitiveKey.toLowerCase()),
    );

    if (isSensitiveKey) {
      redacted[key] = "[REDACTED]";
    } else if (typeof value === "string") {
      redacted[key] = redactPII(value);
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      redacted[key] = redactObjectPII(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      redacted[key] = value.map((item) =>
        typeof item === "string"
          ? redactPII(item)
          : typeof item === "object" && item !== null
            ? redactObjectPII(item as Record<string, unknown>)
            : item,
      );
    } else {
      redacted[key] = value;
    }
  }

  return redacted;
}

export function initializeDatadog(): void {
  if (initialized) {
    return;
  }

  try {
    const appId = process.env.NEXT_PUBLIC_DATADOG_APP_ID;
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
    const env = process.env.NEXT_PUBLIC_NODE_ENV;
    const sharedServicesHost =
      process.env.NEXT_PUBLIC_LL_WEB_SHARED_SERVICES_HOST;
    const encompassApiHost = process.env.NEXT_PUBLIC_LL_WEB_ENCOMPASS_API_HOST;
    const ausApiHost = process.env.NEXT_PUBLIC_LL_WEB_AUS_API_HOST;
    const sharedServicesBase = sharedServicesHost
      ? sharedServicesHost.replace(/\/+$/, "")
      : "";
    const proxyRUM = (options: { path: string; parameters: string }) =>
      `${sharedServicesBase}/datadog${options.path}?${options.parameters}`;
    const proxyLogs = (options: { path: string; parameters: string }) =>
      `${sharedServicesBase}/datadog${options.path}?${options.parameters}`;
    const shouldInitLogs = Boolean(env && env !== "local");

    // Skip Datadog initialization if credentials are not provided (local dev)
    if (!appId || !clientToken) {
      console.log(
        "[Datadog] Skipping initialization - credentials not provided (local dev mode)",
      );
      return;
    }
    console.log("[Datadog] Initializing for env:", env);

    datadogRum.init({
      applicationId: appId,
      clientToken: clientToken,
      site: "datadoghq.com",
      service: "frontend",
      env: env,
      version: process.env.NEXT_PUBLIC_GIT_COMMIT_SHA ?? "unknown",
      // Capture all sessions for error tracking, but no replays
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0, // Disable session replay recording
      // Disable user interaction and performance tracking
      trackUserInteractions: false,
      trackResources: true,
      trackLongTasks: false,
      allowedTracingUrls: [encompassApiHost, ausApiHost].filter(
        (h): h is string => Boolean(h),
      ),
      traceSampleRate: 100,
      // Only track errors and logs
      trackViewsManually: false,
      // Privacy settings for PII protection
      defaultPrivacyLevel: "mask", // Mask all text content and form inputs
      // Scrub sensitive data before sending to Datadog
      beforeSend: (event) => {
        // Redact common PII patterns from error messages and context
        if (event.type === "error" && event.error) {
          event.error.message = redactPII(event.error.message);
          if (event.error.stack) {
            event.error.stack = redactPII(event.error.stack);
          }
        }

        // Redact PII from view URLs (remove query parameters that might contain tokens)
        if (event.view && event.view.url) {
          event.view.url = redactUrlParams(event.view.url);
        }

        // Redact PII from context properties
        if (event.context) {
          event.context = redactObjectPII(event.context);
        }

        return true; // Return false to drop the event entirely
      },
      proxy: proxyRUM,
      usePartitionedCrossSiteSessionCookie: true,
    });

    // Initialize the Logs SDK only in non-local environments and when credentials exist
    if (shouldInitLogs) {
      try {
        datadogLogs.init({
          clientToken: clientToken,
          site: "datadoghq.com",
          env: env,
          service: "frontend",
          // Use explicit Logs intake path
          proxy: proxyLogs,
        });

        logsInitialized = true;
      } catch (e) {
        console.error("[Datadog] Failed to initialize Logs SDK:", e);
      }
    }

    // Do not start session replay recording - we only want errors and logs
    initialized = true;

    console.log("[Datadog] Error and log tracking initialized for env:", env);
  } catch (error) {
    console.error("[Datadog] Failed to initialize RUM:", error);
  }
}

/**
 * Log an error to Datadog
 * @param error - The error object or message
 * @param context - Additional context to attach to the error
 */
export function logError(
  error: Error | string,
  context?: Record<string, unknown>,
): void {
  if (!initialized) {
    // Datadog not initialized (likely local dev) - just log to console

    console.error("[Local] Error:", error, context);
    return;
  }

  if (typeof error === "string") {
    datadogRum.addError(new Error(error), context);
    if (logsInitialized) {
      try {
        const msg = redactPII(error);
        const ctx = context ? redactObjectPII(context) : undefined;
        datadogLogs.logger.error(msg, ctx as Record<string, unknown>);
      } catch (e) {
        console.error("[Datadog] Failed to send log:", e);
      }
    }
  } else {
    datadogRum.addError(error, context);
    if (logsInitialized) {
      try {
        const msg = redactPII(error.message || String(error));
        const ctx = context ? redactObjectPII(context) : {};
        // include stack if present
        if (error.stack) {
          ctx.stack = redactPII(error.stack);
        }
        datadogLogs.logger.error(msg, ctx);
      } catch (e) {
        console.error("[Datadog] Failed to send log:", e);
      }
    }
  }
}

/**
 * Add custom context to the current RUM session
 * Useful for adding user info, transaction IDs, etc.
 * @param key - Context key
 * @param value - Context value
 */
export function addContext(key: string, value: unknown): void {
  if (!initialized) {
    return;
  }
  datadogRum.setGlobalContextProperty(key, value);
}

/**
 * Remove a custom context property
 * @param key - Context key to remove
 */
export function removeContext(key: string): void {
  if (!initialized) {
    return;
  }
  datadogRum.removeGlobalContextProperty(key);
}

/**
 * Log a custom user action to Datadog RUM
 * Useful for tracking business events like "audit_started", "document_uploaded", etc.
 * @param name - Action name (e.g., "audit_started")
 * @param context - Additional context to attach to the action
 */
export function logAction(
  name: string,
  context?: Record<string, unknown>,
): void {
  if (!initialized) {
    console.log("[Local] Action:", name, context);
    return;
  }

  const redactedContext = context ? redactObjectPII(context) : undefined;
  datadogRum.addAction(name, redactedContext);

  // Also log to Datadog Logs for easier querying
  if (logsInitialized) {
    try {
      datadogLogs.logger.info(`Action: ${name}`, redactedContext);
    } catch (e) {
      console.error("[Datadog] Failed to send log:", e);
    }
  }
}

/**
 * Log an informational message to Datadog Logs
 * @param message - Log message
 * @param context - Additional context to attach to the log
 */
export function logInfo(
  message: string,
  context?: Record<string, unknown>,
): void {
  if (!logsInitialized) {
    console.log("[Local] Info:", message, context);
    return;
  }

  try {
    const msg = redactPII(message);
    const ctx = context ? redactObjectPII(context) : undefined;
    datadogLogs.logger.info(msg, ctx);
  } catch (e) {
    console.error("[Datadog] Failed to send log:", e);
  }
}

/**
 * Update session context with current audit/loan identifiers
 * Call this whenever transactionId, auditRunId, or loan context changes
 * This ensures all Datadog events are tagged with consistent identifiers for correlation
 * @param context - Session identifiers (sessionId, loanId, transactionId, transactionStateId, auditRunId)
 */
export function updateSessionContext(context: {
  sessionId?: string | null;
  loanId?: string | null;
  transactionId?: string | null;
  transactionStateId?: string | null;
  auditRunId?: string | null;
}): void {
  if (!initialized) {
    console.log("[Local] Session context:", context);
    return;
  }

  // Set each property individually, removing nulls
  Object.entries(context).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      datadogRum.setGlobalContextProperty(key, value);
    } else {
      datadogRum.removeGlobalContextProperty(key);
    }
  });

  console.log("[Datadog] Session context updated:", context);
}
